import { useState } from 'react';
import { useToast } from '@/context/ToastContext';
import Page from '@/components/page/Page';
import { useRexelShipAndDebitStats } from '@/api/rexel/ship-and-debit/useRexelShipAndDebitStats';
import { useSendWorkbenchTransactions } from '@/api/rexel/ship-and-debit/useSendWorkbenchTransactions';
import { Transaction } from '@/api/rexel/ship-and-debit/useRexelAllTransactions';
import { ShipAndDebitStats } from './components/ShipAndDebitStats';
import { WorkbenchSection } from './components/WorkbenchSection';
import { AvailableTransactionsSection } from './components/AvailableTransactionsSection';
import { TransactionEditModal } from './components/TransactionEditModal';
import { useWorkbench, WorkbenchTransaction } from './hooks/useWorkbench';

export function ShipAndDebitDashboard() {
  const { showToast } = useToast();

  const {
    data: statsData,
    isLoading: isLoadingStats,
    error: statsError,
  } = useRexelShipAndDebitStats();

  const { mutate: sendWorkbenchTransactions, isPending: isSending } =
    useSendWorkbenchTransactions();

  const {
    transactions: workbenchTransactions,
    workbenchIds,
    addToWorkbench,
    removeFromWorkbench,
    updateTransaction,
    markTransactionsAsFailed,
    clearFailureStatus,
    clearWorkbench,
    totalClaimAmount,
    failedCount,
  } = useWorkbench();

  const [editingTransaction, setEditingTransaction] =
    useState<WorkbenchTransaction | null>(null);

  const handleSendAll = () => {
    // Send all transactions in the workbench
    const transactionIds = workbenchTransactions.map((t) => t.id);

    if (transactionIds.length === 0) {
      return;
    }

    sendWorkbenchTransactions(
      { transactionIds },
      {
        onSuccess: (response) => {
          // RPC returns data directly (not wrapped in response.data)
          const { successful, failed, summary } = response;

          // Remove successful transactions from workbench
          if (successful.length > 0) {
            removeFromWorkbench(successful);
          }

          // Mark failed transactions
          if (failed.length > 0) {
            markTransactionsAsFailed(failed);
          }

          // Show appropriate toast message
          if (summary.failedCount === 0) {
            showToast({
              message: `Successfully sent ${summary.successCount} transaction(s)`,
              title: 'Success',
              color: 'success',
              icon: 'check',
            });
          } else if (summary.successCount === 0) {
            showToast({
              message: `Failed to send ${summary.failedCount} transaction(s). Check the workbench for details.`,
              title: 'Error',
              color: 'error',
              icon: 'error',
            });
          } else {
            showToast({
              message: `Sent ${summary.successCount} transaction(s), ${summary.failedCount} failed. Check the workbench for details.`,
              title: 'Partial Success',
              color: 'warning',
              icon: 'warning',
            });
          }
        },
      },
    );
  };

  const handleEditTransaction = (transaction: WorkbenchTransaction) => {
    setEditingTransaction(transaction);
  };

  const handleSaveTransaction = (
    transactionId: string,
    updates: Partial<Transaction>,
  ) => {
    updateTransaction(transactionId, updates);
  };

  const handleAddToWorkbench = (transactions: Transaction[]) => {
    addToWorkbench(transactions);
  };

  return (
    <Page title="Ship and Debit">
      <div className="space-y-6">
        <ShipAndDebitStats
          isLoading={isLoadingStats}
          error={statsError}
          data={statsData}
        />

        <WorkbenchSection
          transactions={workbenchTransactions}
          totalClaimAmount={totalClaimAmount}
          failedCount={failedCount}
          onRemove={removeFromWorkbench}
          onClear={clearWorkbench}
          onEditTransaction={handleEditTransaction}
          onSendAll={handleSendAll}
          onClearFailureStatus={clearFailureStatus}
          isSending={isSending}
        />

        <AvailableTransactionsSection
          workbenchIds={workbenchIds}
          onAddToWorkbench={handleAddToWorkbench}
        />

        <TransactionEditModal
          isOpen={editingTransaction !== null}
          onClose={() => setEditingTransaction(null)}
          transaction={editingTransaction}
          onSave={handleSaveTransaction}
        />
      </div>
    </Page>
  );
}
